import React from 'react';
import clsx from 'clsx';

/**
 * Skeleton — shimmer loader that replaces blocking spinners (LAT-14).
 *
 * Variants:
 *   <Skeleton />                          // single line
 *   <Skeleton w="60%" />
 *   <Skeleton variant="circle" size={32} />
 *   <Skeleton variant="rect" h={120} />
 *   <SkeletonText lines={3} />            // multi-line
 *   <SkeletonRow cols={5} />              // table row
 *   <SkeletonCard />                      // card-shaped block
 *
 * Animation uses a single motion token (`var(--motion-shimmer)`) which is
 * declared globally in tokens.css. Respects prefers-reduced-motion.
 */

const baseStyle = {
  backgroundColor: 'var(--neutral-100)',
  backgroundImage:
    'linear-gradient(90deg, transparent, color-mix(in srgb, var(--white) 60%, transparent) 50%, transparent)',
  backgroundSize: '200% 100%',
  animation: 'bidit-shimmer 1.4s ease-in-out infinite',
};

export default function Skeleton({
  variant = 'line',  // line | circle | rect
  w,
  h,
  size,              // shorthand: sets both w and h (used by circle)
  className,
  style,
  ...rest
}) {
  const widthValue = w ?? size ?? '100%';
  const heightValue =
    h ?? size ?? (variant === 'line' ? '0.875em' : variant === 'circle' ? '2rem' : '1rem');
  const radius =
    variant === 'circle'
      ? '9999px'
      : variant === 'rect'
      ? 'var(--radius-md)'
      : 'var(--radius-sm)';

  return (
    <span
      aria-hidden="true"
      className={clsx('block', className)}
      style={{
        ...baseStyle,
        width: widthValue,
        height: heightValue,
        borderRadius: radius,
        ...style,
      }}
      {...rest}
    />
  );
}

export function SkeletonText({ lines = 3, className }) {
  return (
    <div className={clsx('space-y-2', className)} role="status" aria-label="Loading">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          w={i === lines - 1 ? '70%' : '100%'}
          h="0.875em"
        />
      ))}
    </div>
  );
}

export function SkeletonRow({ cols = 4, className }) {
  return (
    <div
      className={clsx('flex items-center gap-4 py-3', className)}
      role="status"
      aria-label="Loading row"
    >
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} h="1em" w={i === 0 ? '20%' : `${100 / cols - 5}%`} />
      ))}
    </div>
  );
}

export function SkeletonCard({ className }) {
  return (
    <div
      className={clsx('p-4 space-y-3', className)}
      style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
      role="status"
      aria-label="Loading card"
    >
      <Skeleton w="40%" h="1em" />
      <Skeleton w="100%" h="2.5em" variant="rect" />
      <Skeleton w="60%" h="0.875em" />
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4, className }) {
  return (
    <div
      className={clsx('divide-y', className)}
      style={{
        borderColor: 'var(--color-border-subtle)',
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: 'var(--radius-md)',
      }}
      role="status"
      aria-label="Loading table"
    >
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-4">
          <SkeletonRow cols={cols} />
        </div>
      ))}
    </div>
  );
}
